import type { AreaSpawnRules } from '@/combat/spawnDirector';
import type { ItemId } from '@/data/items';

/**
 * Enemy archetypes.
 *
 * Content, not code. Speeds are pixels per second and ranges are pixels, the
 * units the systems work in, because converting tiles at three call sites is
 * how a "why is this one twice as fast" afternoon starts.
 */

export type EnemyId = 'blightMoth' | 'rootGrub';

export interface EnemyDefinition {
  readonly id: EnemyId;
  readonly name: string;
  readonly maxHealth: number;
  /** Pixels per second while chasing. */
  readonly speed: number;
  /** How close Katie must be before it notices, in pixels. */
  readonly aggroRange: number;
  /** Damage dealt by walking into Katie. */
  readonly contactDamage: number;
  /** Seconds before it can hurt her again. */
  readonly contactCooldown: number;
  /** Pixels of knockback taken per hit. */
  readonly knockback: number;
  /**
   * What it leaves behind, and how often.
   *
   * Enemies dropped nothing until the forge needed inputs that came from
   * fighting rather than farming. Kept as a chance rather than a guarantee so
   * a material is something you accumulate, not something you count out.
   */
  readonly drop: { readonly item: ItemId; readonly chance: number };
}

export const ENEMIES: Readonly<Record<EnemyId, EnemyDefinition>> = {
  // Drifts about the fields and closes in fast. Fragile: the first thing you
  // ever kill should die to two swings of a pruning blade.
  blightMoth: {
    id: 'blightMoth',
    name: 'Blight Moth',
    maxHealth: 8,
    speed: 26,
    aggroRange: 88,
    contactDamage: 4,
    contactCooldown: 1,
    knockback: 10,
    drop: { item: 'mat:mothDust', chance: 0.5 },
  },
  // Slower, tougher, and barely notices you until you are close.
  rootGrub: {
    id: 'rootGrub',
    name: 'Root Grub',
    maxHealth: 18,
    speed: 14,
    aggroRange: 56,
    contactDamage: 7,
    contactCooldown: 1.4,
    knockback: 5,
    drop: { item: 'mat:blightSap', chance: 0.45 },
  },
};

/**
 * Which areas are dangerous, and how dangerous.
 *
 * An area absent from this table never spawns anything. **The farm and the
 * cottage are absent deliberately** — home is where you go to stop, and a game
 * that lets something interrupt watering the turnips has taken the one place
 * the player is not being asked to pay attention and made it cost attention.
 * Danger is somewhere you travel to.
 *
 * Repeats in `table` are weight: two entries of a creature make it twice as
 * likely as one. Cheaper to read than a list of pairs, and it keeps the tuning
 * in one column.
 *
 * Night caps are roughly double the day. Anything less and dusk is a lighting
 * change rather than a reason to head home.
 */
export const AREA_SPAWN_RULES: Readonly<Record<string, AreaSpawnRules>> = {
  // Underground there is no day, so both caps are the night figure. The hour
  // above ground is not something you can feel from three floors down.
  dungeon: {
    table: ['rootGrub', 'rootGrub', 'blightMoth'],
    dayCap: 7,
    nightCap: 7,
    intervalSeconds: 9,
    waveSize: 2,
  },
  coast: {
    table: ['blightMoth', 'blightMoth', 'rootGrub'],
    dayCap: 4,
    nightCap: 9,
    intervalSeconds: 12,
    waveSize: 2,
  },
};

export const ENEMY_IDS = Object.keys(ENEMIES) as EnemyId[];

export function isEnemyId(value: unknown): value is EnemyId {
  return typeof value === 'string' && value in ENEMIES;
}
